import { routerActions } from 'connected-react-router'
import React, { PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from '../hook-utils'
import { ContactType, EntityType, ExtensionStatus } from '../model'
import { actions } from '../store'
import { selectMyself } from '../store/reducers/config'
import { selectExtension } from '../store/reducers/queueAgents'
import { selectShortcuts } from '../store/reducers/shortcuts'
import { ExtensionMenuItems } from './AgentMenu'
import { PresenceInfo } from './AgentsTable'
import { getContactIcon } from './AgentView/ContactPage'
import { ContactMenuItems } from './ContactsPage'
import { Menu, SubMenu } from './Menu'
import QueueIcon from './QueueIcon'
import { QueueMenuItems } from './QueueMenu'
import './ShortcutsBar.scss'

function ShortcutButton({ key, menuClassName, children, items, ...props }: PropsWithChildren<React.ButtonHTMLAttributes<HTMLButtonElement> & {
  key: string
  menuClassName?: string
  items: React.ReactElement | null
}>) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLButtonElement>(null)

  const onContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    setOpen(true)
  }

  return <button key={key} className='shortcutButton' onContextMenu={onContextMenu} ref={ref} {...props}>
    {children}
    <Menu className={menuClassName} open={open} onClose={useCallback(() => setOpen(false), [])} target={ref.current}>
      {items}
    </Menu>
  </button>
}

export default function ShortcutsBar() {
  const dispatch = useDispatch()
  const shortcuts = useSelector(selectShortcuts)
  const myId = useSelector(selectMyself)
  const myself = useSelector(s => myId ? selectExtension(s, myId) : undefined)
  const answered = myself && myself.status === ExtensionStatus.Answered

  const items = useMemo(() => shortcuts.map(s => {
    function dialOrTransfer(number: string) {
      if (!number) return
      if (answered) {
        dispatch(actions.ccBlindTransfer(number))
      } else {
        dispatch(actions.ccDial(number))
      }
    }

    switch (s.entityType) {
      case EntityType.Extension:
        return <ShortcutButton key={s.groupListId}
          onClick={() => dialOrTransfer(s.id)}
          items={<ExtensionMenuItems key={'e-sub' + s.id} extension={s}>
            {children => <SubMenu>{children}</SubMenu>}
          </ExtensionMenuItems>}>
          <PresenceInfo extension={s} enabled={true} />{s.name}
        </ShortcutButton>

      case EntityType.Queue: {
        return <ShortcutButton key={s.groupListId}
          onClick={() => {
            dispatch(routerActions.replace('/queues'))
            dispatch(actions.setQueueView({ region: undefined, queue: { id: s.id, server: s.server } }))
          }}
          items={<QueueMenuItems key={'q-sub-' + s.id} queue={s} />}>
          <QueueIcon icon={s.icon} />{s.description}
        </ShortcutButton >
      }

      case EntityType.Contact: {
        return <ShortcutButton key={s.groupListId}
          onClick={() => dialOrTransfer(s.business ? s.business : s.mobile ? s.mobile : s.home ? s.home : '')}
          items={<ContactMenuItems item={s}
            onOpen={(item) => {
              dispatch(routerActions.replace('/directory'))
              dispatch(actions.setDirectoryView({ regionId: item.regionId, entityType: EntityType.Contact, contactType: item.extensionNumber === '-1' ? ContactType.Customer : item.extensionNumber === '0' ? ContactType.Company : ContactType.Personal }))
              dispatch(actions.setSelectedContact(item))
            }} onNew={(item) => {
              dispatch(routerActions.replace('/directory'))
              dispatch(actions.setDirectoryView({ regionId: item.regionId, entityType: EntityType.Contact, contactType: item.extensionNumber === '-1' ? ContactType.Customer : item.extensionNumber === '0' ? ContactType.Company : ContactType.Personal }))
              dispatch(actions.setSelectedContact({ id: '', server: '' }))
            }} />}>
          {getContactIcon(s.extensionNumber)}{s.description}
        </ShortcutButton>
      }

      default: {
        const exhaustive: never = s
        return exhaustive
      }
    }
  }), [answered, dispatch, shortcuts])

  return <div className='ShortcutsBar'>
    {items}
  </div>
}
